import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { InternalLinks } from "@/components/InternalLinks";

const ESPORTS = [
  {
    slug: "cs2",
    label_sv: "CS2-betting",
    label_en: "CS2 betting",
    desc_sv: "Counter-Strike 2 — Major, ESL Pro League och IEM. Match Winner, Map Winner och round handicap.",
    desc_en: "Counter-Strike 2 — the Major, ESL Pro League and IEM. Match Winner, Map Winner and round handicap.",
  },
  {
    slug: "dota2",
    label_sv: "Dota 2-betting",
    label_en: "Dota 2 betting",
    desc_sv: "DPC och The International — den största prispotten i esport.",
    desc_en: "The DPC and The International — the largest prize pool in esports.",
  },
  {
    slug: "valorant",
    label_sv: "Valorant-betting",
    label_en: "Valorant betting",
    desc_sv: "Riots taktiska shooter med agentförmågor. VCT är det officiella turneringssystemet.",
    desc_en: "Riot's tactical shooter with agent abilities. VCT is the official competitive system.",
  },
  {
    slug: "lol",
    label_sv: "League of Legends-betting",
    label_en: "League of Legends betting",
    desc_sv: "MOBA-klassikern med LEC, LCS och Worlds som höjdpunkterna.",
    desc_en: "The MOBA classic with LEC, LCS and Worlds as the seasonal highlights.",
  },
  {
    slug: "rocket-league",
    label_sv: "Rocket League-betting",
    label_en: "Rocket League betting",
    desc_sv: "Bilfotboll i 3v3-format med RLCS som huvudturnering.",
    desc_en: "Car football in a 3v3 format with the RLCS as the main tournament.",
  },
];

export function EsportsHubPage() {
  const { t, lang, prefix } = useI18n();
  const isSv = lang === "sv";
  const { game } = useParams();
  const baseTitle = isSv ? "Esports betting 2026 — guide för Sverige" : "Esports betting 2026 — Sweden guide";
  const baseDesc = isSv
    ? "Esports-betting förklarat — CS2, Dota 2, Valorant, League of Legends och fler titlar."
    : "Esports betting explained — CS2, Dota 2, Valorant, League of Legends and more titles.";
  const base = `${prefix}/${isSv ? "esport-betting" : "esports-betting"}`;

  const title = game ? `${baseTitle} — ${game.replace(/-/g, " ")}` : baseTitle;
  const desc = game ? `${title}. ${baseDesc}` : baseDesc;

  return (
    <div className="container-page py-8">
      <SEO title={title} description={desc} />
      <Breadcrumbs items={[{ label: t("nav.home"), href: prefix }, { label: title }]} />
      <h1 className="mt-3 text-3xl font-extrabold md:text-4xl">{title}</h1>
      <p className="mt-2 max-w-3xl text-ink-700">{desc}</p>

      <article className="prose prose-ink mt-6 max-w-3xl [&>h2]:mt-8 [&>h2]:mb-3 [&>h2]:text-2xl [&>h2]:font-bold [&>p]:my-3">
        <h2>{isSv ? "Vad är esports-betting?" : "What is esports betting?"}</h2>
        <p>
          {isSv
            ? "Esport är professionell datorspelstävling. Betting på esport fungerar som vilken sport som helst — du satsar på matchvinnare, kartor, totalt antal poäng eller specifika spelarprestationer. Marknadsstorleken har vuxit kraftigt sedan 2018 och är idag en stor del av sportbetting-volymen hos flera operatörer."
            : "Esports is professional video game competition. Betting on esports works like any sport — you bet match winner, maps, total points or specific player performance. The market has grown rapidly since 2018 and now makes up a large share of sportsbook volume at several operators."}
        </p>
        <h2>{isSv ? "Hur skiljer det sig från traditionell sport?" : "How is it different from traditional sport?"}</h2>
        <p>
          {isSv
            ? "Patcher kan ändra spelets balans dramatiskt över en natt. Roster-byten är vanligare och kan helt ändra ett lags styrka. Live-odds rör sig snabbare än i fotboll eftersom matcher ofta avgörs av enstaka rundor eller kills. Och meta — vilka strategier som dominerar — förändras varje månad."
            : "Patches can change the game's balance dramatically overnight. Roster swaps are more common and can completely change a team's strength. Live odds move faster than in football because matches are often decided by single rounds or kills. And the meta — which strategies dominate — shifts every month."}
        </p>
      </article>

      <h2 className="mt-10 text-2xl font-bold">
        {isSv ? "Spelguider per titel" : "Per-title guides"}
      </h2>
      <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {ESPORTS.map((e) => (
          <Link
            key={e.slug}
            to={`${base}/${e.slug}`}
            className="card-interactive p-5 no-underline"
          >
            <h3 className="text-lg font-semibold">{isSv ? e.label_sv : e.label_en}</h3>
            <p className="mt-2 text-sm text-ink-600">{isSv ? e.desc_sv : e.desc_en}</p>
            <span className="mt-3 inline-block text-sm font-semibold text-brand-700">
              {isSv ? "Läs mer" : "Read more"} →
            </span>
          </Link>
        ))}
      </div>

      <InternalLinks context="esports" />
    </div>
  );
}
